/**
 * Iter-225s redesign — Client Reputation badge (premium marketplace look).
 *
 * Design direction (per user spec 2026-05-27):
 *   • Feels like Airbnb / Uber / LinkedIn, not a credit score
 *   • Reviewer profile photos drive trust — they are the hero element
 *   • The /100 score is intentionally de-emphasized to a tooltip / footnote
 *   • Stars, review count, "would work again" %, and tier badge form the headline
 *   • Strong social-proof metric "Repeat talent" gets its own callout
 *   • Mobile-first, scannable in seconds
 *
 * Variants:
 *   • compact — small inline row for the gig card
 *   • detail  — full hero + recent feedback list inside the gig detail modal
 */
import React, { useEffect, useState } from 'react';
import {
  Star, ThumbsUp, MessageSquareQuote,
  Trophy, ShieldCheck, Sprout, AlertTriangle,
  Calendar, MapPin, Repeat, Briefcase, Award,
} from 'lucide-react';
import api from '../utils/api';

/* ─────────────────── Tier visual config ─────────────────── */
const TIERS = {
  top_rated: {
    label:    'Top Rated Client',
    icon:     Trophy,
    pill:     'bg-amber-50 border-amber-300 text-amber-900 ring-1 ring-amber-200/60',
    accent:   'text-amber-600',
  },
  trusted: {
    label:    'Trusted Client',
    icon:     ShieldCheck,
    pill:     'bg-emerald-50 border-emerald-300 text-emerald-900',
    accent:   'text-emerald-600',
  },
  new: {
    label:    'New Client',
    icon:     Sprout,
    pill:     'bg-zinc-50 border-zinc-300 text-zinc-700',
    accent:   'text-zinc-500',
  },
  under_review: {
    label:    'Under Review',
    icon:     AlertTriangle,
    pill:     'bg-orange-50 border-orange-300 text-orange-900',
    accent:   'text-orange-600',
  },
};

/* ─────────────────── Shared helpers ─────────────────── */
const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return ''; }
};

const TierPill = ({ tier, size = 'sm' }) => {
  if (!tier || !TIERS[tier]) return null;
  const t = TIERS[tier];
  const Icon = t.icon;
  const cls = size === 'lg'
    ? 'inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1 rounded-full border'
    : 'inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border';
  return (
    <span className={`${cls} ${t.pill}`} data-testid={`client-tier-${tier}`}>
      <Icon className={`${size === 'lg' ? 'w-4 h-4' : 'w-3.5 h-3.5'} ${t.accent}`} />
      {t.label}
    </span>
  );
};

const StarRow = ({ value, size = 'w-4 h-4' }) => {
  if (value == null) return null;
  const rounded = Math.round(value * 2) / 2;
  return (
    <span className="inline-flex items-center" aria-label={`${value} of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`${size} ${n <= rounded ? 'text-amber-400 fill-amber-400' : 'text-zinc-300 fill-zinc-200'}`}
        />
      ))}
    </span>
  );
};

/** Circular reviewer avatar — initials fallback when photo missing. */
const Avatar = ({ photoUrl, name, size = 'w-10 h-10' }) => {
  const initials = (name || '?')
    .split(' ')
    .filter(Boolean)
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={name || 'Reviewer'}
        className={`${size} rounded-full object-cover ring-2 ring-white shadow-sm`}
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
    );
  }
  return (
    <span
      className={`${size} rounded-full bg-gradient-to-br from-zinc-200 to-zinc-300 text-zinc-700 flex items-center justify-center text-xs font-semibold ring-2 ring-white shadow-sm`}
      aria-hidden="true"
    >
      {initials}
    </span>
  );
};

/** Small "Would work again" chip — green check, scannable. */
const WouldWorkAgainChip = () => (
  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full">
    <ThumbsUp className="w-3 h-3 fill-emerald-500 text-emerald-600" />
    Would work again
  </span>
);

/* ─────────────────── Main component ─────────────────── */
export const ClientReputationBadge = ({ clientId, variant = 'compact' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clientId) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    api.get(`/clients/${clientId}/reputation?recent_limit=3`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId]);

  if (loading || !clientId || !data) return null;

  const isEmpty = (data.total_reviews || 0) === 0;
  const tier    = data.tier;
  const score   = data.reputation_score;

  /* ───── Compact — gig card row ─────
     Per user spec: keep score in the background, lead with the social signals.
     We surface tier pill, stars, review count, and WWA%. The score lives in
     a title-tooltip on the tier pill so it's still discoverable but doesn't
     compete visually. */
  if (variant === 'compact') {
    if (isEmpty) {
      return (
        <span
          className="inline-flex items-center gap-2 text-xs"
          data-testid={`client-rep-empty-${clientId}`}
        >
          <TierPill tier="new" />
          <span className="text-zinc-500 italic">No talent feedback yet</span>
        </span>
      );
    }
    return (
      <span
        className="inline-flex items-center gap-2 flex-wrap text-xs"
        data-testid={`client-rep-compact-${clientId}`}
        title={score != null ? `Reputation score: ${score}/100` : undefined}
      >
        {tier && <TierPill tier={tier} />}
        <span className="inline-flex items-center gap-1 text-zinc-800">
          <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
          <span className="font-semibold">{data.avg_rating?.toFixed(1)}</span>
          <span className="text-zinc-500">
            ({data.total_reviews} {data.total_reviews === 1 ? 'review' : 'reviews'})
          </span>
        </span>
        {data.would_work_again_pct != null && (
          <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
            <ThumbsUp className="w-3.5 h-3.5" />
            {data.would_work_again_pct}% would work again
          </span>
        )}
      </span>
    );
  }

  /* ───── Detail — gig detail modal hero ─────
     Layout (mobile-first, premium marketplace):
       1) Hero: logo + name + tier pill + ⭐⭐⭐⭐⭐ + review count + WWA %
       2) Strong-signal metric strip: repeat talent · completed events · talent booked
       3) Recent Talent Feedback — photo + name + role + date + stars + comment + WWA chip */
  return (
    <div
      className="bg-white rounded-2xl border border-zinc-200 overflow-hidden"
      data-testid={`client-rep-detail-${clientId}`}
    >
      {/* === HERO === */}
      <div className="px-5 pt-5 pb-4 bg-gradient-to-b from-zinc-50/80 to-white">
        <div className="flex items-start gap-4">
          {/* Client logo / initial fallback */}
          <Avatar
            photoUrl={data.client_logo_url}
            name={data.client_name}
            size="w-14 h-14"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3
                className="text-lg font-bold text-zinc-900 truncate"
                data-testid="client-rep-name"
              >
                {data.client_name || 'This client'}
              </h3>
              {tier && <TierPill tier={tier} size="lg" />}
            </div>
            {!isEmpty ? (
              <div className="mt-1 flex items-center gap-3 flex-wrap text-sm">
                <span className="inline-flex items-center gap-1.5">
                  <StarRow value={data.avg_rating} size="w-4 h-4" />
                  <span className="font-semibold text-zinc-900">
                    {data.avg_rating?.toFixed(1)}
                  </span>
                  <span className="text-zinc-500">
                    ({data.total_reviews} {data.total_reviews === 1 ? 'review' : 'reviews'})
                  </span>
                </span>
                {data.would_work_again_pct != null && (
                  <span className="inline-flex items-center gap-1 text-emerald-700">
                    <ThumbsUp className="w-4 h-4" />
                    <span className="font-semibold">{data.would_work_again_pct}%</span>
                    <span className="text-emerald-700/70">would work again</span>
                  </span>
                )}
              </div>
            ) : (
              <div className="mt-1 text-sm text-zinc-500 italic">
                No talent feedback yet for this client.
              </div>
            )}
          </div>
        </div>

        {/* Strong-signal metric strip — only when we have anything meaningful */}
        {!isEmpty && (
          <div className="mt-4 grid grid-cols-3 gap-2">
            <div
              className="rounded-xl bg-white border border-zinc-200 p-3 text-center"
              data-testid="client-rep-metric-repeat"
            >
              <div className="inline-flex items-center justify-center gap-1 text-zinc-500 text-[11px] uppercase tracking-wide font-medium">
                <Repeat className="w-3 h-3" /> Repeat talent
              </div>
              <div className="mt-0.5 text-xl font-bold text-zinc-900">
                {data.repeat_talent_count || 0}
              </div>
            </div>
            <div
              className="rounded-xl bg-white border border-zinc-200 p-3 text-center"
              data-testid="client-rep-metric-events"
            >
              <div className="inline-flex items-center justify-center gap-1 text-zinc-500 text-[11px] uppercase tracking-wide font-medium">
                <Calendar className="w-3 h-3" /> Events
              </div>
              <div className="mt-0.5 text-xl font-bold text-zinc-900">
                {data.completed_events_count || 0}
              </div>
            </div>
            <div
              className="rounded-xl bg-white border border-zinc-200 p-3 text-center"
              data-testid="client-rep-metric-booked"
            >
              <div className="inline-flex items-center justify-center gap-1 text-zinc-500 text-[11px] uppercase tracking-wide font-medium">
                <Award className="w-3 h-3" /> Talent booked
              </div>
              <div className="mt-0.5 text-xl font-bold text-zinc-900">
                {data.talent_booked_count || 0}
              </div>
            </div>
          </div>
        )}

        {/* Repeat-talent callout — the strongest single trust signal per user spec.
            Only render when ≥2 to avoid noise. */}
        {!isEmpty && (data.repeat_talent_count || 0) >= 2 && (
          <div
            className="mt-3 inline-flex items-center gap-2 text-sm text-emerald-800 bg-emerald-50/70 border border-emerald-100 px-3 py-2 rounded-lg"
            data-testid="client-rep-repeat-callout"
          >
            <Repeat className="w-4 h-4 text-emerald-600" />
            <span>
              <span className="font-semibold">{data.repeat_talent_count}</span> talent have
              worked with this client more than once.
            </span>
          </div>
        )}
      </div>

      {/* === RECENT TALENT FEEDBACK === */}
      {!isEmpty && (data.recent_reviews || []).length > 0 && (
        <div className="px-5 pb-5">
          <div className="flex items-center justify-between mb-3 mt-1">
            <h4 className="text-sm font-semibold text-zinc-800 inline-flex items-center gap-1.5">
              <MessageSquareQuote className="w-4 h-4 text-zinc-500" />
              Recent Talent Feedback
            </h4>
            {/* Score deliberately demoted to a quiet footnote */}
            {score != null && (
              <span
                className="text-[11px] text-zinc-400 font-medium"
                title="Composite reputation score (0–100). Bayesian-weighted, debiased for sample size."
              >
                Reputation score: {score}/100
              </span>
            )}
          </div>
          <ul className="space-y-4">
            {(data.recent_reviews || []).map((rv, idx) => (
              <li
                key={idx}
                className="flex items-start gap-3"
                data-testid={`client-rep-review-${idx}`}
              >
                <Avatar
                  photoUrl={rv.reviewer_photo_url}
                  name={rv.reviewer_short_name || rv.reviewer_display || 'Verified Talent'}
                  size="w-11 h-11"
                />
                <div className="flex-1 min-w-0">
                  {/* Identity + role + event meta */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="text-sm font-semibold text-zinc-900"
                      data-testid={`client-rep-review-${idx}-name`}
                    >
                      {rv.reviewer_short_name || rv.reviewer_display || 'Verified Talent'}
                    </span>
                    {rv.would_work_again === true && <WouldWorkAgainChip />}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5 flex items-center flex-wrap gap-x-1.5 gap-y-0.5">
                    {rv.role_name && (
                      <span className="inline-flex items-center gap-1">
                        <Briefcase className="w-3 h-3" />
                        {rv.role_name}
                      </span>
                    )}
                    {(rv.city || rv.event_date) && <span>•</span>}
                    {rv.city && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {rv.city}{rv.state ? `, ${rv.state}` : ''}
                      </span>
                    )}
                    {rv.event_date && (
                      <>
                        <span>•</span>
                        <span>{formatDate(rv.event_date)}</span>
                      </>
                    )}
                  </div>
                  {/* Star row */}
                  <div className="mt-1.5">
                    <StarRow value={rv.stars} size="w-3.5 h-3.5" />
                  </div>
                  {/* Comment — generous quote treatment */}
                  <p className="mt-1.5 text-sm text-zinc-800 leading-relaxed">
                    “{rv.comment}”
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ClientReputationBadge;
